import axios from 'axios';
import { isTokenExpired, clearAuthAndRedirect } from '../utils/tokenUtils';

// Function to get CSRF token from cookies
export const getCsrfToken = () => {
  // First try to get from cookies (Django's default approach)
  const name = 'csrftoken=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');

  for (let i = 0; i < cookieArray.length; i++) {
    let cookie = cookieArray[i].trim();
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length, cookie.length);
    }
  }

  // Fallback to meta tag if available
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) {
    return metaTag.getAttribute('content');
  }

  return '';
};

// Initialize API client with CSRF support
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || `${window.location.origin}/api/`,
  //baseURL: "https://azure.omnirises.com/api/",
  withCredentials: true, // This is important for CSRF
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  }
});

// console.log(api);

// Add a request interceptor to include CSRF token
api.interceptors.request.use(async (config) => {
  // Set Accept-Language header to support backend localization
  const language = localStorage.getItem('i18nextLng') || 'en';
  config.headers['Accept-Language'] = language;

  // Get CSRF token from cookie

  const csrfToken = document.cookie.split('; ')
    .find(row => row.startsWith('csrftoken='))
    ?.split('=')[1];

  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken;
  }

  // Add Bearer token if it exists and check if it's expired (unless skipAuth is set)
  const token = localStorage.getItem('token');
  if (token && !config.skipAuth) {
    // Check if token is expired before making the request
    if (isTokenExpired(token)) {
      console.log('Token expired during request, logging out automatically');

      // Clear auth and redirect
      clearAuthAndRedirect();

      // Reject the request
      return Promise.reject(new Error('Token expired'));
    }

    config.headers['Authorization'] = `Bearer ${token}`;
  }

  // Handle multipart/form-data requests
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  } else if (config.method !== 'get') {
    config.headers['Content-Type'] = 'application/json';
  }
  return config;
});

// Add a response interceptor for error handling
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      const isLoginRequest = error.config.url.includes('auth/login/') ||
        error.config.url.includes('auth/google/') ||
        error.config.url.includes('auth/verify-identifier/');

      // Check if user is currently on login pages
      const isOnLoginPage = window.location.pathname.includes('/login') ||
        window.location.pathname.includes('/login') ||
        window.location.pathname.includes('/signup') ||
        window.location.pathname.includes('/reset-password');

      // Check if user is on interview invite pages (should not require authentication)
      const isOnInterviewInvitePage = window.location.pathname.includes('/interview/invite/') ||
        window.location.pathname.includes('/interview/session/invite/') ||
        window.location.pathname.includes('/interview/start');

      // Check if user is on public job pages (should not require authentication)
      const isOnPublicJobPage = window.location.pathname.match(/^\/jobs\/\d{4}\/\d{1,2}\/\d{1,2}\/[^/]+\/?$/) ||
        window.location.pathname.includes('/publicjobdescriptions');

      // Check if this is a request that should be allowed to fail without redirecting
      const isOptionalAuthRequest = error.config.url.includes('check-application-status/') ||
        error.config.url.includes('/preview-match/') ||
        error.config.url.includes('public-jobs/') ||
        error.config.skipAuth ||
        error.config.allowUnauth;

      // Handle JWT token expiration - but NOT for login requests, login pages, interview invite pages, public pages, or optional auth requests
      if (error.response.status === 401 &&
        !isLoginRequest &&
        !isOnLoginPage &&
        !isOnInterviewInvitePage &&
        !isOnPublicJobPage &&
        !isOptionalAuthRequest) {
        // Clear auth and redirect
        clearAuthAndRedirect();
        return Promise.reject(new Error('Session expired. Please login again.'));
      }

      // Handle CSRF token expiration - but NOT when on login pages, interview invite pages, or public pages
      if (error.response.status === 403 &&
        error.response.data?.detail?.includes('CSRF') &&
        !isOnLoginPage &&
        !isOnInterviewInvitePage &&
        !isOnPublicJobPage) {
        // Refresh the page to get a new CSRF token only if not on login/interview/public pages
        window.location.reload();
        return Promise.reject(new Error('CSRF validation failed. Please refresh the page and try again.'));
      }

      console.error('API Response Error:', {
        url: error.config.url,
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
    }

    // For login requests, login pages, public pages, or other errors, let the component handle them
    return Promise.reject(error);
  }
);

// Small utility for manual token setting if needed
api.setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `${token}`;
    // console.log('Auth token set in API client');
  } else {
    delete api.defaults.headers.common['Authorization'];
    console.log('Auth token removed from API client');
  }
};

// Skill analysis and JD matching functions
export const getSemanticRanking = async (jobId, params = {}) => {
  try {
    const searchParams = new URLSearchParams(params);
    const response = await api.get(`job-descriptions/${jobId}/semantic-ranking/?${searchParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching semantic ranking:', error);
    throw error;
  }
};

export const triggerSemanticMatching = async (jobId) => {
  try {
    const response = await api.post(`job-descriptions/${jobId}/trigger-semantic-matching/`);
    return response.data;
  } catch (error) {
    console.error('Error triggering semantic matching:', error);
    throw error;
  }
};

export const getJdMatchingStatus = async (jobId) => {
  const response = await api.get(`job-descriptions/${jobId}/matching-status/`);
  return response.data;
};

export const getInterviewReportStatus = async (sessionId) => {
  const response = await api.get(`sessions/${sessionId}/report-status/`);
  return response.data;
};

export const getCandidateInterviewsDigest = async (candidateId, { page = 1, pageSize = 10, showArchived = false } = {}) => {
  const params = new URLSearchParams({
    interview_page: String(page),
    interview_page_size: String(pageSize),
  });
  if (showArchived) params.set('show_archived', 'true');
  const response = await api.get(`candidates/${candidateId}/interviews-digest/?${params.toString()}`);
  return response.data;
};

export const analyzeSkills = async (candidateId, jobDescriptionId) => {
  try {
    const response = await api.post('admin/analyze-skills/', {
      candidate_id: candidateId,
      job_description_id: jobDescriptionId
    });
    return response.data;
  } catch (error) {
    console.error('Error analyzing skills:', error);
    throw error;
  }
};

export const reanalyzeSkills = async (candidateId, jobDescriptionId, source = 'both') => {
  try {
    const response = await api.post('admin/reanalyze-skills/', {
      candidate_id: candidateId,
      job_description_id: jobDescriptionId,
      source: source
    });
    return response.data;
  } catch (error) {
    console.error('Error re-analyzing skills:', error);
    throw error;
  }
};

export const clearAllSkillAnalysisCaches = async () => {
  try {
    const response = await api.post('admin/clear-skill-analysis-cache/', {});
    return response.data;
  } catch (error) {
    console.error('Error clearing skill analysis caches:', error);
    throw error;
  }
};

export const createInviteWithSkills = async (candidateId, jobDescriptionId, selectedSkills = []) => {
  try {
    const response = await api.post('admin/create-invite-with-skills/', {
      candidate_id: candidateId,
      job_description_id: jobDescriptionId,
      selected_skills: selectedSkills
    });
    return response.data;
  } catch (error) {
    console.error('Error creating invite with skills:', error);
    throw error;
  }
};

// Skill Search & Bulk Actions
export const skillSearch = async (skills, n = 20) => {
  try {
    const response = await api.post('admin/candidates/skill-search/', {
      skills,
      n
    });
    return response.data;
  } catch (error) {
    console.error('Error performing skill search:', error);
    throw error;
  }
};

export const exportReport = async (candidateIds, skills) => {
  try {
    const response = await api.post('admin/candidates/export-report/', {
      candidate_ids: candidateIds,
      skills
    }, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting report:', error);
    throw error;
  }
};

// TTS APIs
export const generateQuestionAudio = async (text, voiceName = 'en-US-JennyNeural', speed = 'normal') => {
  try {
    const response = await api.post('tts/generate-audio/', {
      text: text,
      voice_name: voiceName,
      speed: speed
    });
    return response.data;
  } catch (error) {
    console.error('Error generating question audio:', error);
    throw error;
  }
};

// Public Jobs APIs
export const getPublicJobs = async () => {
  try {
    const response = await api.get('public-jobs/', {
      headers: {
        'Authorization': `Token ${localStorage.getItem('adminToken')}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching public jobs:', error);
    throw error;
  }
};

export const previewJobMatch = async (jobId, resumeId) => {
  try {
    const response = await api.get(`public-jobs/${jobId}/preview-match/?resume_id=${resumeId}`);

    return response.data;
  } catch (error) {
    // If user is not authenticated (401), throw a more specific error
    if (error.response?.status === 401) {
      throw new Error('Please log in to preview job match');
    }
    console.error('Error previewing job match:', error);
    throw error;
  }
};

// PDF Generation API
export const generatePDFFromHTML = async (htmlContent, sessionId, filename, compactMode = false) => {
  try {
    const response = await api.post('/generate-pdf/', {
      html_content: htmlContent,
      session_id: sessionId,
      filename: filename,
      compact_mode: compactMode
    }, {
      responseType: 'blob' // Important for PDF download
    });
    return response.data;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

// Public Job API Functions (no authentication required)
export const getPublicJobBySlug = async (year, month, day, slug) => {
  try {
    // Use the main api client but skip auth for this endpoint
    const response = await api.get(`jobs/${year}/${month}/${day}/${slug}/`, {
      skipAuth: true,
      allowUnauth: true
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching public job:', error);
    throw error;
  }
};

export const getPublicJobPreview = async (year, month, day, slug) => {
  try {
    const response = await api.get(`jobs/${year}/${month}/${day}/${slug}/preview/`, {
      skipAuth: true,
      allowUnauth: true
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching public job preview:', error);
    throw error;
  }
};

export const applyToExternalPracticeJob = async (jobData) => {
  try {
    const response = await api.post('practice-jobs/external/apply/', jobData);
    return response.data;
  } catch (error) {
    console.error('Error applying to external practice job:', error);
    throw error;
  }
};

// Job Application APIs
export const checkApplicationStatus = async (jobId) => {
  try {
    const response = await api.get(`check-application-status/${jobId}/`, {
      allowUnauth: true
    });
    return response.data;
  } catch (error) {
    // If user is not authenticated (401), return a default response instead of throwing
    if (error.response?.status === 401) {
      return {
        has_applied: false,
        can_apply: true,
        reason: 'Please log in to check application status'
      };
    }
    console.error('Error checking application status:', error);
    throw error;
  }
};

export const applyForJob = async (applicationData) => {
  try {
    const response = await api.post('apply-for-job/', applicationData);
    return response.data;
  } catch (error) {
    console.error('Error applying for job:', error);
    throw error;
  }
};

// Delete functions
export const deleteJobApplication = async (applicationId) => {
  try {
    const response = await api.delete(`job-applications/${applicationId}/delete_application/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting job application:', error);
    throw error;
  }
};

export const deleteJobDescription = async (jobDescriptionId) => {
  try {
    const response = await api.delete(`job-descriptions/${jobDescriptionId}/delete_job_description/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting job description:', error);
    throw error;
  }
};

export const expireInvite = async (token) => {
  try {
    const response = await api.post(`admin/expire-invite/${token}/`);
    return response.data;
  } catch (error) {
    console.error('Error expiring invite:', error);
    throw error;
  }
};

export const resendInterviewInviteEmail = async (inviteId) => {
  try {
    const response = await api.post('admin/send-interview-invite-email/', {
      inviteId: inviteId
    });
    return response.data;
  } catch (error) {
    console.error('Error resending interview invite email:', error);
    throw error;
  }
};

export const getBulkSchedulePendingInvites = async (jdId, { search = '' } = {}) => {
  const params = new URLSearchParams({ jd_id: String(jdId) });
  if (search) {
    params.set('search', search);
  }
  const response = await api.get(`interviews/bulk-schedule/pending-invites/?${params.toString()}`);
  return response.data;
};

export const sendBulkInviteReminders = async (jdId, inviteIds) => {
  const response = await api.post('interviews/bulk-schedule/send-reminders/', {
    jd_id: jdId,
    invite_ids: inviteIds,
  });
  return response.data;
};

// End interview session (removes Redis TTL and marks as completed)
export const endInterview = async (sessionId) => {
  try {
    const response = await api.post(`sessions/${sessionId}/finish_interview/`);
    return response.data;
  } catch (error) {
    console.error('Error ending interview:', error);
    throw error;
  }
};

export const resolveMediaUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/?api\/?$/i, '');
  const base = apiBase || (process.env.REACT_APP_FRONTEND_URL || '').replace(/\/$/, '');
  return base ? `${base}${url.startsWith('/') ? '' : '/'}${url}` : url;
};

// Generate JD from Audio Recording or Transcript (dispatches background task, returns immediately)
export const generateJDFromAudio = async (formData, onUploadProgress) => {
  const response = await api.post('job-descriptions/generate-from-audio/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000, // 120s — file uploads over the network need more time than API calls
    onUploadProgress,  // optional callback: (progressEvent) => { ... }
  });
  return response.data; // { audit_id, status: 'pending', message }
};

// ── Azure Blob Direct Upload (bypasses Django for file payload) ──────────────

/**
 * Step 1: Get a SAS URL from the backend for direct browser → Azure upload.
 *
 * @param {string} filename  - Original filename (e.g. 'recording.mp3')
 * @param {string} fileType  - 'audio' | 'video' | 'transcript'
 * @param {number} companyId - Company PK
 * @returns {{ upload_url: string, blob_path: string }}
 */
export const getAzureUploadUrl = async (filename, fileType, companyId) => {
  const response = await api.post('storage/upload-url/', {
    filename,
    file_type: fileType,
    company_id: companyId,
  });
  return response.data;
};

/**
 * Step 2: Upload file directly to Azure Blob Storage using the SAS URL.
 * Uses XHR (not Axios) for reliable upload progress tracking.
 *
 * @param {string}   sasUrl      - Full SAS URL from getAzureUploadUrl
 * @param {File}     file        - File object to upload
 * @param {Function} onProgress  - Callback: (percent: number) => void
 * @returns {Promise<void>}
 */
export const uploadToAzureBlob = (sasUrl, file, onProgress) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.open('PUT', sasUrl);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');  // mandatory for Azure Blob PUT
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.onload = () => {
      if (xhr.status === 201) {
        resolve();
      } else {
        reject(new Error(`Azure upload failed with status ${xhr.status}: ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during Azure upload'));
    xhr.ontimeout = () => reject(new Error('Azure upload timed out'));
    xhr.timeout = 600000; // 10 min — handles very large files

    xhr.send(file);
  });
};

/**
 * Step 3: Tell the backend the file is in Azure Blob, dispatch Celery task.
 *
 * @param {string} blobPath - The blob_path returned from getAzureUploadUrl
 * @param {string} language - ISO-639-1 code (e.g. 'en', 'hi')
 * @returns {{ audit_id: number, status: string, message: string }}
 */
export const generateJDFromBlob = async (blobPath, language, companyName = '') => {
  const response = await api.post('job-descriptions/generate-from-audio/', {
    blob_path: blobPath,
    language,
    company_name: companyName,
  });
  return response.data;
};

// Poll the status of a background JD generation
export const pollJDGenerationStatus = async (auditId) => {
  const response = await api.get(`job-descriptions/generate-from-audio/${auditId}/status/`);
  return response.data; // { status } or { status: 'done', structured_description, ... }
};

// Fetch user's recent JD generations using backend pagination only.
export const getMyJDGenerations = async ({ page = 1, pageSize = 10 } = {}) => {
  const response = await api.get('job-descriptions/my-jd-generations/', {
    params: {
      page,
      page_size: pageSize,
    },
  });

  const data = response.data;
  const results = Array.isArray(data?.results) ? data.results : [];
  const count = Number.isFinite(data?.count) ? data.count : 0;
  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  if (!Array.isArray(data?.results) || !Number.isFinite(data?.count)) {
    throw new Error('JD drafts endpoint must return paginated data.');
  }

  return {
    results,
    count,
    page,
    pageSize,
    totalPages,
    hasNext: Boolean(data?.next) || page < totalPages,
    hasPrevious: Boolean(data?.previous) || page > 1,
  };
};

// Cancel an ongoing JD generation task
export const cancelJDGeneration = async (auditId) => {
  const response = await api.post(`job-descriptions/generate-from-audio/${auditId}/cancel/`);
  return response.data;
};

// Delete a JD generation draft
export const deleteJDGeneration = async (auditId) => {
  const response = await api.delete(`job-descriptions/generate-from-audio/${auditId}/delete/`);
  return response.data;
};

// Resume Analysis APIs
export const getResumeAnalysis = async (jobDescriptionId = null) => {
  const params = jobDescriptionId ? `?job_description_id=${jobDescriptionId}` : '';
  const response = await api.get(`resumes/analysis/${params}`);
  return response.data;
};

export const runResumeAnalysis = async (resumeId = null, jobDescriptionId = null) => {
  const payload = {};
  if (resumeId) payload.resume_id = resumeId;
  if (jobDescriptionId) payload.job_description_id = jobDescriptionId;
  const response = await api.post('resumes/full-analysis/', payload);
  return response.data;
};

export default api;

